// CookieConsent.js
import React, { useState, useEffect } from 'react';
import { FaCookieBite, FaCheck, FaTimes, FaChevronDown, FaChevronUp, FaShieldAlt, FaDatabase, FaChartLine, FaAd } from 'react-icons/fa';

const CookieConsent = ({ onAccept, onDecline, forceOpen }) => {
    const [visible, setVisible] = useState(false);
    const [isPolicyOpen, setIsPolicyOpen] = useState(false);
    const [preferences, setPreferences] = useState({
        necessary: true, // Toujours true, non modifiable
        functional: false,
        analytics: false,
        advertising: false
    });

    useEffect(() => {
        if (forceOpen) {
            setVisible(true);
        }
    }, [forceOpen]);

    useEffect(() => {
        const consentGiven = localStorage.getItem('cookieConsent');
        const savedPreferences = localStorage.getItem('cookiePreferences');
        const consentDate = localStorage.getItem('cookieConsentDate');

        const COOKIE_EXPIRATION_DAYS = 180; // 6 mois

        if (consentDate) {
            const savedDate = new Date(consentDate);
            const now = new Date();

            const diffDays =
                (now.getTime() - savedDate.getTime()) /
                (1000 * 60 * 60 * 24);

            if (diffDays > COOKIE_EXPIRATION_DAYS) {
                localStorage.removeItem('cookieConsent');
                localStorage.removeItem('cookiePreferences');
                localStorage.removeItem('cookieConsentDate');

                setVisible(true);
                return;
            }
        }

        if (!consentGiven) {
            setVisible(true);
        } else if (savedPreferences) {
            setPreferences(JSON.parse(savedPreferences));
        }
    }, []);

    // useEffect(() => {
    //     const consentGiven = localStorage.getItem('cookieConsent');
    //     const savedPreferences = localStorage.getItem('cookiePreferences');
    //     const consentDate = localStorage.getItem('cookieConsentDate');

    //     const COOKIE_EXPIRATION_MS = 2 * 60 * 1000; // 2 minutes

    //     if (consentDate) {
    //         const savedDate = new Date(consentDate);
    //         const now = new Date();

    //         const diffMs = now.getTime() - savedDate.getTime();

    //         if (diffMs > COOKIE_EXPIRATION_MS) {
    //             localStorage.removeItem('cookieConsent');
    //             localStorage.removeItem('cookiePreferences');
    //             localStorage.removeItem('cookieConsentDate');

    //             setVisible(true);
    //             return;
    //         }
    //     }

    //     if (!consentGiven) {
    //         setVisible(true);
    //     } else if (savedPreferences) {
    //         setPreferences(JSON.parse(savedPreferences));
    //     }
    // }, []);

    const handleAcceptAll = () => {
        const allAccepted = {
            necessary: true,
            functional: true,
            analytics: true,
            advertising: true
        };
        savePreferences(allAccepted);
        if (onAccept) onAccept(allAccepted);
        localStorage.setItem('cookieConsent', 'accepted');
    };

    const handleDeclineAll = () => {
        const allDeclined = {
            necessary: true,
            functional: false,
            analytics: false,
            advertising: false
        };
        savePreferences(allDeclined);
        if (onDecline) onDecline(allDeclined);
        disableTrackingCookies();
        localStorage.setItem('cookieConsent', 'declined');
    };

    const handleSavePreferences = () => {
        savePreferences(preferences);
        if (onAccept) onAccept(preferences);

        if (preferences.analytics) {
            enableAnalyticsCookies();
        } else {
            disableAnalyticsCookies();
        }

        if (preferences.advertising) {
            enableAdvertisingCookies();
        } else {
            disableAdvertisingCookies();
        }
        localStorage.setItem('cookieConsent', 'customized');
    };

    const savePreferences = (prefs) => {
        localStorage.setItem('cookieConsent', 'customized');
        localStorage.setItem('cookiePreferences', JSON.stringify(prefs));
        localStorage.setItem('cookieConsentDate', new Date().toISOString());
        setVisible(false);
    };

    const disableTrackingCookies = () => {
        disableAnalyticsCookies();
        disableAdvertisingCookies();
    };

    const disableAnalyticsCookies = () => {
        if (window.gtag) {
            window['ga-disable-UA-XXXXXX-Y'] = true;
        }
    };

    const enableAnalyticsCookies = () => {
        if (window.gtag) {
            window['ga-disable-UA-XXXXXX-Y'] = false;
            window.gtag('consent', 'update', {
                'analytics_storage': 'granted'
            });
        }
    };

    const disableAdvertisingCookies = () => {
        if (window.gtag) {
            window.gtag('consent', 'update', {
                'ad_storage': 'denied'
            });
        }
    };

    const enableAdvertisingCookies = () => {
        if (window.gtag) {
            window.gtag('consent', 'update', {
                'ad_storage': 'granted'
            });
        }
    };

    const togglePreference = (type) => {
        if (type === 'necessary') return;
        setPreferences(prev => ({
            ...prev,
            [type]: !prev[type]
        }));
    };

    if (!visible) return null;

    return (
        <>
            {/* Overlay de la modale */}
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 9998,
                    animation: 'fadeIn 0.3s ease-out'
                }}
                onClick={() => { }} // Empêche la fermeture en cliquant sur l'overlay
            />

            {/* Modale */}
            <div
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '90%',
                    maxWidth: '700px',
                    maxHeight: '85vh',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                    zIndex: 9999,
                    display: 'flex',
                    flexDirection: 'column',
                    animation: 'modalSlideIn 0.3s ease-out',
                    overflow: 'hidden'
                }}
            >
                {/* En-tête avec titre */}
                <div style={{
                    padding: '20px 24px',
                    borderBottom: '1px solid #e9ecef',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            backgroundColor: '#e8f5e9',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <FaCookieBite style={{ fontSize: '20px', color: '#355deb' }} />
                        </div>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#212529' }}>
                                Gestion des cookies
                            </h3>
                            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6c757d' }}>
                                Nous respectons votre vie privée
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => setVisible(false)}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#6c757d',
                            padding: '5px',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '4px',
                            transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        ×
                    </button>
                </div>

                {/* Corps de la modale avec défilement */}
                <div style={{
                    padding: '20px 24px',
                    flex: 1,
                    overflowY: 'auto',
                    maxHeight: 'calc(85vh - 180px)'
                }}>
                    {/* Description principale */}
                    <p style={{ marginBottom: '20px', fontSize: '14px', color: '#495057', lineHeight: '1.6' }}>
                        Nous utilisons des cookies pour améliorer votre expérience, analyser le trafic et personnaliser le contenu.
                        Certains cookies sont nécessaires au bon fonctionnement du site.
                    </p>

                    {/* Bouton pour ouvrir/fermer la politique détaillée */}
                    <button
                        onClick={() => setIsPolicyOpen(!isPolicyOpen)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#355deb',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 0',
                            marginBottom: '16px',
                            fontWeight: '500',
                            width: '100%',
                            justifyContent: 'space-between',
                            borderBottom: '1px solid #e9ecef'
                        }}
                    >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {isPolicyOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                            {isPolicyOpen ? 'Masquer la politique détaillée' : 'Voir la politique détaillée des cookies'}
                        </span>
                    </button>

                    {/* Volet pliable avec la politique détaillée */}
                    {isPolicyOpen && (
                        <div style={{
                            marginBottom: '20px',
                            padding: '16px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            border: '1px solid #e9ecef',
                            animation: 'slideDown 0.3s ease-out'
                        }}>
                            <h4 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '16px', color: '#212529' }}>
                                Politique détaillée des cookies
                            </h4>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {/* Cookie nécessaire */}
                                <div style={{
                                    padding: '12px',
                                    backgroundColor: 'white',
                                    borderRadius: '8px',
                                    border: '1px solid #dee2e6'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <FaShieldAlt style={{ color: '#355deb', fontSize: '20px' }} />
                                            <div>
                                                <strong>Cookies nécessaires</strong>
                                                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6c757d' }}>
                                                    Essentiels pour le fonctionnement du site. Ne peuvent pas être désactivés.
                                                </p>
                                            </div>
                                        </div>
                                        <span style={{
                                            backgroundColor: '#355deb',
                                            color: 'white',
                                            padding: '4px 10px',
                                            borderRadius: '20px',
                                            fontSize: '11px',
                                            fontWeight: 'bold'
                                        }}>
                                            Toujours actif
                                        </span>
                                    </div>
                                </div>

                                {/* Cookies fonctionnels */}
                                <div style={{
                                    padding: '12px',
                                    backgroundColor: 'white',
                                    borderRadius: '8px',
                                    border: '1px solid #dee2e6'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <FaDatabase style={{ color: '#007bff', fontSize: '20px' }} />
                                            <div>
                                                <strong>Cookies fonctionnels</strong>
                                                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6c757d' }}>
                                                    Mémorisent vos préférences (langue, région) pour une expérience personnalisée.
                                                </p>
                                            </div>
                                        </div>
                                        <label style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            cursor: 'pointer',
                                            padding: '4px 8px',
                                            borderRadius: '20px',
                                            backgroundColor: '#f8f9fa'
                                        }}>
                                            <input
                                                type="checkbox"
                                                checked={preferences.functional}
                                                onChange={() => togglePreference('functional')}
                                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                            />
                                            <span style={{ fontSize: '13px' }}>Activer</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Cookies analytics */}
                                <div style={{
                                    padding: '12px',
                                    backgroundColor: 'white',
                                    borderRadius: '8px',
                                    border: '1px solid #dee2e6'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <FaChartLine style={{ color: '#17a2b8', fontSize: '20px' }} />
                                            <div>
                                                <strong>Cookies analytics</strong>
                                                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6c757d' }}>
                                                    Nous aident à comprendre comment les visiteurs interagissent avec le site.
                                                </p>
                                            </div>
                                        </div>
                                        <label style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            cursor: 'pointer',
                                            padding: '4px 8px',
                                            borderRadius: '20px',
                                            backgroundColor: '#f8f9fa'
                                        }}>
                                            <input
                                                type="checkbox"
                                                checked={preferences.analytics}
                                                onChange={() => togglePreference('analytics')}
                                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                            />
                                            <span style={{ fontSize: '13px' }}>Activer</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Pied de la modale avec boutons */}
                <div style={{
                    padding: '16px 24px',
                    borderTop: '1px solid #e9ecef',
                    backgroundColor: '#f8f9fa',
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end',
                    flexWrap: 'wrap'
                }}>
                    <button
                        onClick={handleDeclineAll}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: 'white',
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s ease',
                            color: '#495057'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                            e.currentTarget.style.borderColor = '#cbd3da';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'white';
                            e.currentTarget.style.borderColor = '#dee2e6';
                        }}
                    >
                        <FaTimes size={14} /> Refuser tout
                    </button>

                    <button
                        onClick={handleSavePreferences}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#5a6268';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#6c757d';
                        }}
                    >
                        <FaCheck size={14} /> Enregistrer mes préférences
                    </button>

                    <button
                        onClick={handleAcceptAll}
                        style={{
                            padding: '10px 24px',
                            backgroundColor: '#355deb',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#218838';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#355deb';
                        }}
                    >
                        <FaCheck size={14} /> Accepter tout
                    </button>
                </div>
            </div>

            {/* Styles d'animation */}
            <style>
                {`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          
          @keyframes modalSlideIn {
            from {
              opacity: 0;
              transform: translate(-50%, -48%);
            }
            to {
              opacity: 1;
              transform: translate(-50%, -50%);
            }
          }
          
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          /* Style pour la scrollbar */
          .cookie-modal-content::-webkit-scrollbar {
            width: 8px;
          }
          
          .cookie-modal-content::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
          }
          
          .cookie-modal-content::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 10px;
          }
          
          .cookie-modal-content::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
        `}
            </style>
        </>
    );
};

export default CookieConsent;